import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiCheck, FiFolder, FiMessageSquare, FiUsers } from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import { listProjectMembers, listProjects } from '../utils/conversationApi';
import { KpfAvatar, KpfSuggestionList, useKpfSuggestions } from './KpfEmailSuggestions';
import './ShareConversationModal.css';

const CLOSE_ANIMATION_MS = 220;

const ROLE_LABELS = { owner: 'Owner', edit: 'Edit', view: 'View' };

// Past this many projects the list gets a box to find one by name.
const PROJECT_FILTER_MIN = 7;

const ACCESS_PHRASES = { edit: 'Can edit', view: 'Can view' };

// The line under a project's name in the "Add to a project" list.
function projectMeta(project) {
  if (project.shared) {
    const from = project.ownerless ? 'Owner has left' : 'Shared with you';
    return `${from} · ${ACCESS_PHRASES[project.my_role] || 'Can view'}`;
  }
  const joined = project.shared_with || 0;
  return joined > 0 ? `Shared with ${joined} ${joined === 1 ? 'person' : 'people'}` : 'Only you';
}

// "a@kpf.com", "a@kpf.com and b@kpf.com", "a@kpf.com, b@kpf.com and 2 others".
function listNames(names) {
  if (names.length <= 2) return names.join(' and ');
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} others`;
}

const KPF_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@kpf\.com$/i;

function parseEmails(input) {
  return input
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

const isKpfEmail = (email) => KPF_EMAIL_REGEX.test(email);

// Splits what's in the box into finished recipients and the text still being
// typed. A comma, semicolon or newline always ends a recipient; a space ends
// one only after an email address, so a full name ("Alex Cabana") can still
// be typed to search for a colleague.
function splitDraft(value) {
  const pieces = value.split(/[,;\n]/);
  let draft = pieces.pop().replace(/^\s+/, '');
  const done = pieces.flatMap((piece) => {
    const trimmed = piece.trim();
    return trimmed.includes('@') ? trimmed.split(/\s+/) : [trimmed];
  });
  let match;
  while ((match = /^(\S*@\S*)\s+/.exec(draft))) {
    done.push(match[1]);
    draft = draft.slice(match[0].length);
  }
  return { done: done.filter(Boolean), draft };
}

/**
 * Share a conversation by email. A conversation in a project (`projectId`)
 * can be shared with that project's members only -- it can quote the
 * project's files and instructions -- so instead of an open "To" box it lists
 * the members to pick from, or explains that there is no one to share with
 * yet. The server enforces the same rule (conversation_routes.py's share
 * routes); this is the explanation, not the boundary.
 *
 * A conversation outside any project can instead be added to one of the
 * user's projects ("Add to a project"), when `onAddToProject` is given. It
 * moves there, and its later turns work like any of the project's own.
 */
export default function ShareConversationModal({
  isOpen,
  onClose,
  conversationName,
  conversationId,
  onShare,
  projectId = null,
  projectName = '',
  // The owner or an Edit member: may add people to the project.
  canManageMembers = false,
  // Opens the project's Manage members dialog (this one closes first).
  onManageMembers,
  // (conversationId, project) => Promise of the server's reply: moves the
  // conversation into `project`.
  onAddToProject,
  // (reply, project) => void, once the move is done and this dialog has gone.
  onAddedToProject,
}) {
  const { user } = useAuth();
  // Recipients added so far, shown as chips: { email, display_name?, profile_img? }
  // -- a colleague picked from the suggestions keeps their name and photo.
  const [recipients, setRecipients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sharedCount, setSharedCount] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  // Project conversations only: everyone on the project (pending invites
  // flagged), and the members ticked so far, by lower-cased email.
  const [members, setMembers] = useState(null);
  const [membersError, setMembersError] = useState('');
  const [membersReload, setMembersReload] = useState(0);
  const [selectedEmails, setSelectedEmails] = useState([]);
  // Conversations outside a project: 'people' (share by email) or 'project'
  // (add it to one of the user's projects), and that tab's project list.
  const [mode, setMode] = useState('people');
  const [projects, setProjects] = useState(null);
  const [projectsError, setProjectsError] = useState('');
  const [projectsReload, setProjectsReload] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectFilter, setProjectFilter] = useState('');
  const inputRef = useRef(null);
  const fieldRef = useRef(null);
  // Someone already added shouldn't be offered again.
  const suggest = useKpfSuggestions({
    exclude: (candidate) => recipients.some(
      (r) => r.email.toLowerCase() === candidate.email.toLowerCase(),
    ),
  });

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      suggest.reset();
    }
    // suggest.reset is a stable callback; this is about opening the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !projectId) return undefined;
    let cancelled = false;
    setMembers(null);
    setMembersError('');
    setSelectedEmails([]);
    listProjectMembers(projectId)
      .then((list) => { if (!cancelled) setMembers(list); })
      .catch((err) => { if (!cancelled) setMembersError(err.message || 'Could not load project members.'); });
    return () => { cancelled = true; };
  }, [isOpen, projectId, membersReload]);

  // Read fresh each time the tab opens, so a project shared with the user
  // since the page loaded is there too.
  const showingProjects = isOpen && !projectId && mode === 'project';
  useEffect(() => {
    if (!showingProjects) return undefined;
    let cancelled = false;
    setProjects(null);
    setProjectsError('');
    listProjects()
      .then((list) => {
        if (cancelled) return;
        setProjects(list);
        // Only one to choose from: chosen already.
        if (list.length === 1) setSelectedProjectId(list[0].project_id);
      })
      .catch((err) => { if (!cancelled) setProjectsError(err.message || 'Could not load your projects.'); });
    return () => { cancelled = true; };
  }, [showingProjects, projectsReload]);

  if (!isOpen) return null;

  const isProjectConversation = Boolean(projectId);
  const canAddToProject = !isProjectConversation && typeof onAddToProject === 'function';
  const isProjectMode = canAddToProject && mode === 'project';
  // Your own projects first, then the ones shared with you -- the Projects
  // menu's order.
  const orderedProjects = [
    ...(projects || []).filter((p) => !p.shared),
    ...(projects || []).filter((p) => p.shared),
  ];
  const projectFilterTerm = projectFilter.trim().toLowerCase();
  const visibleProjects = projectFilterTerm
    ? orderedProjects.filter((p) => (p.name || '').toLowerCase().includes(projectFilterTerm))
    : orderedProjects;
  // Only while it's in view: one hidden by the filter isn't added unseen.
  const selectedProject = visibleProjects.find((p) => p.project_id === selectedProjectId) || null;
  const projectsLoading = isProjectMode && projects === null && !projectsError;
  const noProjects = isProjectMode && projects !== null && projects.length === 0;
  const myEmail = (user?.email || '').toLowerCase();
  // Who this conversation can go to: everyone who has joined the project,
  // apart from yourself. An invite not yet accepted gives no access, so it
  // only gets a mention.
  const shareableMembers = (members || []).filter((m) => (
    !m.pending && m.email && !(user && m.user_id === user.id) && m.email.toLowerCase() !== myEmail
  ));
  const pendingInvites = (members || []).filter((m) => m.pending && m.email);
  const membersLoading = isProjectConversation && members === null && !membersError;
  const noMembers = isProjectConversation && members !== null && shareableMembers.length === 0;
  const allSelected = shareableMembers.length > 0 && selectedEmails.length === shareableMembers.length;

  const toggleMember = (email) => {
    setError('');
    setSelectedEmails((prev) => (
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    ));
  };

  const toggleAllMembers = () => {
    setError('');
    setSelectedEmails(allSelected ? [] : shareableMembers.map((m) => m.email.toLowerCase()));
  };

  const addRecipients = (entries) => {
    if (entries.length === 0) return;
    setError('');
    setRecipients((prev) => {
      const next = [...prev];
      entries.forEach((entry) => {
        const key = entry.email.toLowerCase();
        if (!next.some((r) => r.email.toLowerCase() === key)) next.push(entry);
      });
      return next;
    });
  };

  const removeRecipient = (email) => {
    setRecipients((prev) => prev.filter((r) => r.email !== email));
    setError('');
    inputRef.current?.focus();
  };

  // Turns whatever is typed into a chip -- Enter, or leaving the box with an
  // address in it.
  const commitDraft = () => {
    const emails = parseEmails(inputValue);
    if (emails.length === 0) return;
    addRecipients(emails.map((email) => ({ email })));
    setInputValue('');
    suggest.reset();
  };

  const handleShare = async () => {
    // An address still being typed counts too, so Share doesn't need an
    // Enter first.
    const seen = new Set();
    const emails = (isProjectConversation
      ? selectedEmails
      : [...recipients.map((r) => r.email), ...parseEmails(inputValue)]
    ).filter((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (emails.length === 0) {
      setError(isProjectConversation
        ? 'Choose at least one member.'
        : 'Add at least one @kpf.com email address.');
      return;
    }
    const invalid = emails.filter((e) => !isKpfEmail(e));
    if (invalid.length > 0) {
      setError(`Only @kpf.com addresses are allowed. Invalid: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '...' : ''}`);
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onShare(conversationId, emails);
      setRecipients([]);
      setInputValue('');
      setSelectedEmails([]);
      setSharedCount(emails.length);
    } catch (err) {
      setError(err.message || 'Failed to share conversation.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { done, draft } = splitDraft(e.target.value);
    addRecipients(done.map((email) => ({ email })));
    setInputValue(draft);
    suggest.search(draft);
  };

  const handleKeyDown = (e) => {
    // Arrow keys / Enter-on-a-highlighted-row / Escape belong to the
    // suggestion list while it is open.
    if (suggest.handleKeyDown(e, handleSelectSuggestion)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      const top = suggest.freshTop();
      const typed = inputValue.trim();
      if (typed.includes('@')) {
        // A typed address is added as typed -- with the top match's name and
        // photo only when that match is this exact address.
        if (top && top.email.toLowerCase() === typed.toLowerCase()) handleSelectSuggestion(top);
        else commitDraft();
      } else if (top) {
        // A name ("rizwana", "andrew coll") means the person it matches.
        handleSelectSuggestion(top);
      }
      return;
    }
    if (e.key === 'Backspace' && inputValue === '' && recipients.length > 0) {
      e.preventDefault();
      setRecipients((prev) => prev.slice(0, -1));
      setError('');
    }
  };

  const handleBlur = () => {
    suggest.close();
    // Only something that looks like an address becomes a chip on leaving
    // the box; half a name is left as typed rather than turned into a red chip.
    if (inputValue.includes('@')) commitDraft();
  };

  const handleSelectSuggestion = (candidate) => {
    addRecipients([{
      email: candidate.email,
      display_name: candidate.display_name,
      profile_img: candidate.profile_img,
    }]);
    setInputValue('');
    suggest.reset();
    inputRef.current?.focus();
  };

  // `then`: run once this dialog has gone, e.g. opening Manage members.
  const closeDialog = (then) => {
    suggest.reset();
    setIsClosing(true);
    setTimeout(() => {
      setError('');
      setRecipients([]);
      setInputValue('');
      setSelectedEmails([]);
      setSharedCount(null);
      setMode('people');
      setSelectedProjectId(null);
      setProjectFilter('');
      setLoading(false);
      onClose();
      if (typeof then === 'function') then();
    }, CLOSE_ANIMATION_MS);
  };

  const handleClose = (then) => {
    if (loading || isClosing) return;
    closeDialog(then);
  };

  const switchMode = (next) => {
    if (loading || next === mode) return;
    setError('');
    setMode(next);
  };

  // Done: the dialog closes and the conversation opens in the project, where
  // it now is -- there is nothing more to do here.
  const handleAddToProject = async () => {
    if (!selectedProject) {
      setError('Choose a project.');
      return;
    }
    setError('');
    setLoading(true);
    let reply;
    try {
      reply = await onAddToProject(conversationId, selectedProject);
    } catch (err) {
      setError(err.message || 'Could not add the conversation to the project.');
      setLoading(false);
      return;
    }
    const project = selectedProject;
    closeDialog(() => onAddedToProject?.(reply, project));
  };

  const showSuccess = sharedCount !== null;
  const hasInvalid = !isProjectConversation && recipients.some((r) => !isKpfEmail(r.email));
  const isEmpty = isProjectConversation
    ? selectedEmails.length === 0
    : recipients.length === 0 && inputValue.trim() === '';
  const showManageMembers = noMembers && canManageMembers && Boolean(onManageMembers);

  const renderProjectBody = () => {
    if (membersError) {
      return (
        <div className="share-members-status">
          <p className="share-modal-error">{membersError}</p>
          <button
            type="button"
            className="share-members-link"
            onClick={() => setMembersReload((n) => n + 1)}
          >
            Try again
          </button>
        </div>
      );
    }
    if (membersLoading) {
      return <p className="share-members-status share-modal-hint">Loading project members...</p>;
    }
    if (noMembers) {
      const pendingNames = pendingInvites.map((m) => m.display_name || m.email);
      return (
        <div className="share-members-empty">
          <div className="share-members-empty-icon" aria-hidden="true">
            <FiUsers size={22} />
          </div>
          <p className="share-members-empty-title">
            {pendingNames.length > 0 ? 'No one has joined this project yet' : 'This project has no members yet'}
          </p>
          <p className="share-members-empty-text">
            A conversation in a project can only be shared with the project&apos;s members.
            {pendingNames.length > 0
              ? ` ${listNames(pendingNames)} ${pendingNames.length === 1 ? 'has' : 'have'} been invited. You can share this conversation once they accept.`
              : ' Add members with View or Edit access, and you can then share this conversation with them.'}
          </p>
          {!canManageMembers && (
            <p className="share-members-empty-text share-members-empty-text--muted">
              Only the project owner or a member with Edit access can add people.
            </p>
          )}
        </div>
      );
    }
    return (
      <>
        <div className="share-members-head">
          <span id="share-members-label">Project members</span>
          {shareableMembers.length > 1 && (
            <button type="button" className="share-members-link" onClick={toggleAllMembers} disabled={loading}>
              {allSelected ? 'Clear' : 'Select all'}
            </button>
          )}
        </div>
        <ul className="share-members-list" aria-labelledby="share-members-label">
          {shareableMembers.map((m) => {
            const key = m.email.toLowerCase();
            const checked = selectedEmails.includes(key);
            return (
              <li key={key}>
                <label className={`share-member${checked ? ' share-member--checked' : ''}${loading ? ' share-member--disabled' : ''}`}>
                  <input
                    type="checkbox"
                    className="share-member-check"
                    checked={checked}
                    onChange={() => toggleMember(key)}
                    disabled={loading}
                  />
                  <KpfAvatar candidate={m} />
                  <span className="share-member-text">
                    <span className="share-member-name">{m.display_name || m.email}</span>
                    {m.display_name && <span className="share-member-email">{m.email}</span>}
                  </span>
                  <span className="share-member-role">{ROLE_LABELS[m.role] || 'View'}</span>
                </label>
              </li>
            );
          })}
        </ul>
        {error ? (
          <p className="share-modal-error">{error}</p>
        ) : (
          <p className="share-modal-hint">
            Only members of {projectName ? `"${projectName}"` : 'this project'} can open it. They get an email with a link.
            {pendingInvites.length > 0 && ` ${pendingInvites.length === 1 ? '1 invitation is' : `${pendingInvites.length} invitations are`} still pending; you can share with them once they accept.`}
          </p>
        )}
      </>
    );
  };

  const renderAddToProjectBody = () => {
    if (projectsError) {
      return (
        <div className="share-members-status">
          <p className="share-modal-error">{projectsError}</p>
          <button
            type="button"
            className="share-members-link"
            onClick={() => setProjectsReload((n) => n + 1)}
          >
            Try again
          </button>
        </div>
      );
    }
    if (projectsLoading) {
      return <p className="share-members-status share-modal-hint">Loading your projects...</p>;
    }
    if (noProjects) {
      return (
        <div className="share-members-empty">
          <div className="share-members-empty-icon" aria-hidden="true">
            <FiFolder size={22} />
          </div>
          <p className="share-members-empty-title">You don&apos;t have any projects yet</p>
          <p className="share-members-empty-text">
            Create one from the Projects menu, then add this conversation to it.
          </p>
        </div>
      );
    }
    return (
      <>
        {orderedProjects.length >= PROJECT_FILTER_MIN && (
          <input
            type="search"
            className="share-projects-filter"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            placeholder="Find a project"
            aria-label="Find a project"
            disabled={loading}
            autoComplete="off"
          />
        )}
        <div className="share-members-head">
          <span id="share-projects-label">Projects</span>
        </div>
        {visibleProjects.length === 0 ? (
          <p className="share-members-status share-modal-hint">No project matches &quot;{projectFilter.trim()}&quot;.</p>
        ) : (
          <ul className="share-members-list" aria-labelledby="share-projects-label">
            {visibleProjects.map((p) => {
              const checked = p.project_id === selectedProjectId;
              return (
                <li key={p.project_id}>
                  <label className={`share-member${checked ? ' share-member--checked' : ''}${loading ? ' share-member--disabled' : ''}`}>
                    <input
                      type="radio"
                      name="share-add-to-project"
                      className="share-member-check"
                      checked={checked}
                      onChange={() => { setError(''); setSelectedProjectId(p.project_id); }}
                      disabled={loading}
                    />
                    <span className="share-project-icon" aria-hidden="true">
                      <FiFolder size={15} />
                    </span>
                    <span className="share-member-text">
                      <span className="share-member-name">{p.name || 'Untitled project'}</span>
                      <span className="share-member-email">{projectMeta(p)}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {error ? (
          <p className="share-modal-error">{error}</p>
        ) : (
          <>
            <p className="share-modal-hint">
              It moves into the project&apos;s Conversations, where only you can see it. From your next
              message, KAI looks in the project&apos;s files and earlier conversations first.
            </p>
            <p className="share-modal-hint">
              Anyone you shared it with before can still open it only if they&apos;re in the project.
            </p>
          </>
        )}
      </>
    );
  };

  return (
    <div
      className={`share-modal-overlay ${isClosing ? 'share-modal-overlay--closing' : ''}`}
      onClick={handleClose}
    >
      <div
        className={`share-modal ${isClosing ? 'share-modal--closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="share-modal-header">
          <h2>{showSuccess ? 'Conversation shared' : 'Share conversation'}</h2>
          <button type="button" className="share-modal-close" onClick={() => handleClose()} aria-label="Close">
            <FiX size={20} />
          </button>
        </div>
        {showSuccess ? (
          <div className="share-modal-body share-modal-success-body share-modal-success-body--enter">
            <div className="share-modal-success-icon">
              <FiCheck size={32} />
            </div>
            <p className="share-modal-success-message">
              The conversation has been shared with {sharedCount} recipient{sharedCount !== 1 ? 's' : ''}.
            </p>
            <p className="share-modal-success-sub">They will receive an email with a link to open it.</p>
          </div>
        ) : (
          <div className="share-modal-body">
            {canAddToProject && (
              <div className="share-modal-tabs" role="tablist" aria-label="Share or add to a project">
                {[
                  { key: 'people', label: 'Share with people', Icon: FiUsers },
                  { key: 'project', label: 'Add to a project', Icon: FiFolder },
                ].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={mode === key}
                    className={`share-modal-tab${mode === key ? ' share-modal-tab--active' : ''}`}
                    onClick={() => switchMode(key)}
                    disabled={loading && mode !== key}
                  >
                    <Icon size={14} aria-hidden="true" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            )}
            {conversationName && (
              <p className="share-modal-subtitle" title={conversationName}>
                <FiMessageSquare size={13} aria-hidden="true" />
                <span>{conversationName}</span>
              </p>
            )}
            {isProjectConversation ? renderProjectBody() : isProjectMode ? renderAddToProjectBody() : (
              <>
                {/* Clicking anywhere in the box (between chips, the empty end)
                    puts the caret in the input, as in a mail client's To field. */}
                <div
                  ref={fieldRef}
                  className={`share-modal-field${loading ? ' share-modal-field--disabled' : ''}`}
                  onClick={() => inputRef.current?.focus()}
                >
                  {recipients.map((r) => {
                    const invalid = !isKpfEmail(r.email);
                    const label = r.display_name || r.email;
                    return (
                      <span
                        key={r.email}
                        className={`share-chip${invalid ? ' share-chip--invalid' : ''}`}
                        title={invalid ? `${r.email} is not a @kpf.com address` : r.email}
                      >
                        {r.display_name && <KpfAvatar candidate={r} />}
                        <span className="share-chip-label">{label}</span>
                        <button
                          type="button"
                          className="share-chip-remove"
                          onClick={(e) => { e.stopPropagation(); removeRecipient(r.email); }}
                          disabled={loading}
                          aria-label={`Remove ${label}`}
                        >
                          <FiX size={12} />
                        </button>
                      </span>
                    );
                  })}
                  <input
                    ref={inputRef}
                    type="text"
                    className="share-modal-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={suggest.reopen}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={recipients.length === 0 ? 'Add people by name or email' : ''}
                    disabled={loading}
                    autoComplete="off"
                    autoFocus
                    aria-label="Recipients"
                    role="combobox"
                    aria-expanded={suggest.open}
                    aria-controls={suggest.listId}
                    aria-activedescendant={suggest.activeOptionId}
                    aria-autocomplete="list"
                  />
                </div>
                {suggest.open && (
                  <KpfSuggestionList
                    anchorRef={fieldRef}
                    suggestions={suggest.suggestions}
                    activeIndex={suggest.activeIndex}
                    onHover={suggest.setActiveIndex}
                    onSelect={handleSelectSuggestion}
                    listId={suggest.listId}
                  />
                )}
                {error ? (
                  <p className="share-modal-error">{error}</p>
                ) : (
                  <p className={`share-modal-hint${hasInvalid ? ' share-modal-hint--invalid' : ''}`}>
                    {hasInvalid
                      ? 'Remove the addresses in red. Only @kpf.com addresses can be added.'
                      : 'Only @kpf.com addresses. They get an email with a link.'}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        <div className="share-modal-footer">
          {showSuccess ? (
            <button type="button" className="share-modal-submit" onClick={() => handleClose()}>
              Done
            </button>
          ) : isProjectMode && (noProjects || projectsError) ? (
            <button type="button" className="share-modal-submit" onClick={() => handleClose()}>
              Close
            </button>
          ) : isProjectMode ? (
            <>
              <button type="button" className="share-modal-cancel" onClick={() => handleClose()} disabled={loading}>
                Cancel
              </button>
              <button
                type="button"
                className="share-modal-submit"
                onClick={handleAddToProject}
                disabled={loading || projectsLoading || !selectedProject}
              >
                {loading ? 'Adding...' : 'Add to project'}
              </button>
            </>
          ) : noMembers || membersError ? (
            <>
              <button
                type="button"
                className={showManageMembers ? 'share-modal-cancel' : 'share-modal-submit'}
                onClick={() => handleClose()}
              >
                Close
              </button>
              {showManageMembers && (
                <button type="button" className="share-modal-submit" onClick={() => handleClose(onManageMembers)}>
                  Manage members
                </button>
              )}
            </>
          ) : (
            <>
              <button type="button" className="share-modal-cancel" onClick={() => handleClose()} disabled={loading}>
                Cancel
              </button>
              <button
                type="button"
                className="share-modal-submit"
                onClick={handleShare}
                disabled={loading || membersLoading || isEmpty || hasInvalid}
              >
                {loading ? 'Sharing...' : 'Share'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
